import { ORDERED_LIST, UNORDERED_LIST } from '@lexical/markdown';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { InitialEditorStateType } from '@lexical/react/LexicalComposer';
import * as styles from './lexicalConfig.css';
import { useRef } from 'react';
import { MentionNode } from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionNode';
import { ExtendedTextNode } from '@mutiny-pkg/bin-ui/src/richtext/ExtendedTextPlugin/ExtendedTextNode';
import { TextNode, createCommand, LexicalCommand, ParagraphNode, LexicalEditor } from 'lexical';
import { CustomParagraphNode } from '@mutiny-pkg/bin-ui/src/richtext/CustomParagraphNode/CustomParagraphNode';
import { RichInputVariant } from '@mutiny-pkg/champaign-schemas/src/schemas/richText';
import { LookupNode } from '../LookupPlugin/LookupNode';
import { CustomLinkNode } from '@mutiny-pkg/bin-ui/src/richtext/CustomLinkNode/CustomLinkNode';
import { toEditorState } from '../ast';

export const CLOSE_TOOLBAR: LexicalCommand<boolean> = createCommand();
export const CLOSE_COLOR_MENU: LexicalCommand<boolean> = createCommand();
export const CLOSE_LINK_EDITOR: LexicalCommand<boolean> = createCommand();

export const SUPPORTED_MARKDOWN_TRANSFORMERS = [ORDERED_LIST, UNORDERED_LIST];

type GenerateLexicalConfigProps = {
  variant?: RichInputVariant;
  editorState?: InitialEditorStateType;
  editable?: boolean;
};

function generateLexicalConfig({
  variant = RichInputVariant.InlineEditing,
  editorState,
  editable,
}: GenerateLexicalConfigProps = {}) {
  return {
    namespace: 'MutinyRichInput',
    nodes: [
      MentionNode,
      LookupNode,
      ListItemNode,
      ListNode,
      CustomLinkNode,
      {
        replace: LinkNode,
        with: (node: LinkNode) => {
          return new CustomLinkNode(node.getURL());
        },
        withKlass: CustomLinkNode,
      },
      AutoLinkNode,
      ExtendedTextNode,
      CustomParagraphNode,
      {
        replace: TextNode,
        with: (node: TextNode) => {
          return new ExtendedTextNode(node.getTextContent());
        },
        withKlass: ExtendedTextNode,
      },
      {
        replace: ParagraphNode,
        with: () => {
          return new CustomParagraphNode();
        },
        withKlass: CustomParagraphNode,
      },
    ],
    onError(error: Error, editor: LexicalEditor) {
      console.error(error);

      editor.update(() => {
        editor.setEditorState(editor.parseEditorState(toEditorState('')));
      });
    },
    theme: {
      paragraph: styles.block[variant],
      text: {
        bold: styles.bold,
        italic: styles.italic,
        strikethrough: styles.strikethrough,
        underline: styles.underline,
      },

      list: {
        nested: {
          listitem: styles.listItem,
        },
        ol: styles.orderedList,
        ul: styles.unorderedList,
        listitem: styles.listItem,
      },

      link: styles.link,
      mention: styles.mention,
    },
    editorState,
    editable,
  };
}

export function useLexicalConfig(props: GenerateLexicalConfigProps) {
  return useRef(generateLexicalConfig(props)).current;
}
